import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Package, Save, Send, Info, X, ImagePlus, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  PRODUCT_CATEGORIES,
  SUBCATEGORIAS,
  LICENCIAS_MKT,
  MKT_GOLD,
  MKT_TINT,
} from "../../lib/marketplace";
import { apiGetProductos, apiCrearProducto, apiEditarProducto, apiSubirArchivo } from "../../lib/marketplaceApi";
import { humanizeAxiosError } from "../../lib/api";
import { Field, TextInput, TextArea, SelectInput, ImageUpload, FileUpload, Stepper, SELLER_NOTICE } from "./mktui";
import ComisionResumen from "./ComisionResumen";

const PASOS = ["Información básica", "Archivos y contenido", "Precio y publicación"];

export default function CrearProducto() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const editId = params.get("id");
  const [existente, setExistente] = useState(null);

  const [paso, setPaso] = useState(0);
  const [exito, setExito] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [form, setForm] = useState(
    {
      titulo: "", descCorta: "", descCompleta: "", categoria: "", subcategoria: "", etiquetas: "",
      cover: "", galeria: [], archivoNombre: "", archivo: "", enlaceExterno: "", instrucciones: "", version: "",
      precio: "", precioPromo: "", licencia: "", autoria: false,
    },
  );
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!editId) return;
    (async () => {
      try {
        const items = await apiGetProductos();
        const p = items.find((x) => x.id === editId);
        if (p) {
          setExistente(p);
          setForm({
            titulo: p.titulo || "", descCorta: p.descCorta || "", descCompleta: p.descCompleta || "",
            categoria: p.categoria || "", subcategoria: p.subcategoria || "", etiquetas: p.etiquetas || "",
            cover: p.cover || "", galeria: p.galeria || [], archivoNombre: p.archivoNombre || "", archivo: p.archivo || "",
            enlaceExterno: p.enlaceExterno || "", instrucciones: p.instrucciones || "", version: p.version || "",
            precio: p.precio ?? "", precioPromo: p.precioPromo ?? "", licencia: p.licencia || "", autoria: !!p.autoria,
          });
        }
      } catch (e) { /* noop */ }
    })();
  }, [editId]);

  const addGaleria = (dataUrl) => set("galeria", [...form.galeria, dataUrl].slice(0, 6));
  const rmGaleria = (i) => set("galeria", form.galeria.filter((_, idx) => idx !== i));

  const validarPaso = () => {
    if (paso === 0) {
      if (!form.titulo.trim()) return "Ingresa el título del producto.";
      if (!form.categoria) return "Selecciona una categoría.";
    }
    return null;
  };

  const siguiente = () => {
    const err = validarPaso();
    if (err) return toast.error(err);
    setPaso((p) => Math.min(p + 1, PASOS.length - 1));
  };

  const payloadFrom = () => ({
    ...form,
    precio: form.precio === "" ? 0 : Number(form.precio),
    precioPromo: form.precioPromo === "" || form.precioPromo == null ? null : Number(form.precioPromo),
  });

  const guardar = async (estado) => {
    if (!form.titulo.trim()) return toast.error("Ingresa el título del producto.");
    if (estado === "en_revision") {
      if (!form.categoria) return toast.error("Selecciona una categoría.");
      if (!form.precio) return toast.error("Ingresa el precio en soles.");
      if (!form.autoria) return toast.error("Debes confirmar la autoría y derechos.");
    }
    setGuardando(true);
    try {
      if (existente) await apiEditarProducto(existente.id, payloadFrom(), estado === "en_revision" ? "en_revision" : undefined);
      else await apiCrearProducto(payloadFrom(), estado);
      if (estado === "borrador") {
        toast.success("Producto guardado como borrador.");
        navigate("/marketplace/productos");
      } else {
        setExito(true);
      }
    } catch (e) {
      toast.error(humanizeAxiosError(e));
    } finally {
      setGuardando(false);
    }
  };

  const galeriaRef = React.useRef(null);
  const [subiendoGaleria, setSubiendoGaleria] = useState(false);
  const readGaleria = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setSubiendoGaleria(true);
    try {
      const res = await apiSubirArchivo(f, "product_gallery");
      addGaleria(res.url);
    } catch (err) {
      toast.error(humanizeAxiosError(err));
    } finally {
      setSubiendoGaleria(false);
      if (galeriaRef.current) galeriaRef.current.value = "";
    }
  };

  if (exito) {
    return (
      <div className="min-h-[calc(100vh-4rem)] bg-slate-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-3xl border border-slate-200 shadow-soft p-8 text-center" data-testid="producto-exito">
          <span className="w-16 h-16 rounded-full mx-auto flex items-center justify-center" style={{ backgroundColor: "#FEF3C7", color: "#B45309" }}>
            <CheckCircle2 size={34} />
          </span>
          <h1 className="mt-5 text-2xl font-extrabold text-slate-900">¡Producto enviado a revisión!</h1>
          <p className="mt-2 text-sm text-slate-500">
            Nuestro equipo revisará tu producto antes de publicarlo. Podrás ver su estado en <b>Mis Productos</b>.
          </p>
          <button onClick={() => navigate("/marketplace/productos")} data-testid="producto-exito-btn" className="mt-7 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-white font-bold shadow-sm hover:brightness-110 transition-all" style={{ backgroundColor: MKT_GOLD }}>
            Ir a Mis Productos
          </button>
        </div>
      </div>
    );
  }

  const subcats = (SUBCATEGORIAS[form.categoria] || []).map((s) => ({ value: s, label: s }));

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-slate-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="crear-producto-page">
        <button onClick={() => navigate("/marketplace/productos")} className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800" data-testid="cp-volver-top">
          <ChevronLeft size={16} /> Volver a Mis Productos
        </button>

        <div className="mt-5 flex items-center gap-4">
          <span className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-sm" style={{ backgroundColor: MKT_TINT, color: MKT_GOLD }}>
            <Package size={26} />
          </span>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: MKT_GOLD }}>ConstruLink Marketplace</p>
            <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight">{existente ? "Editar producto" : "Publicar producto digital"}</h1>
          </div>
        </div>

        <div className="mt-4 flex items-start gap-2 text-xs text-slate-500 bg-white border border-slate-200 rounded-xl p-3">
          <Info size={15} className="mt-0.5 flex-shrink-0" style={{ color: MKT_GOLD }} />
          {SELLER_NOTICE} Solo se permiten productos digitales (no servicios, materiales físicos ni mano de obra).
        </div>

        <div className="mt-6 bg-white rounded-2xl border border-slate-200 shadow-soft p-5">
          <Stepper pasos={PASOS} actual={paso} />
        </div>

        <div className="mt-5 bg-white rounded-2xl border border-slate-200 shadow-soft p-6 space-y-5">
          {paso === 0 && (
            <div className="space-y-5" data-testid="paso-basica">
              <Field label="Título del producto" required testid="cp-titulo-field">
                <TextInput value={form.titulo} onChange={(v) => set("titulo", v)} placeholder="Ej. Plantilla de metrados y APU" testid="cp-titulo" />
              </Field>
              <Field label="Descripción corta" testid="cp-desccorta-field">
                <TextInput value={form.descCorta} onChange={(v) => set("descCorta", v)} placeholder="Resumen en una línea" testid="cp-desccorta" />
              </Field>
              <Field label="Descripción completa" testid="cp-desccompleta-field">
                <TextArea value={form.descCompleta} onChange={(v) => set("descCompleta", v)} rows={4} placeholder="Explica qué incluye tu producto…" testid="cp-desccompleta" />
              </Field>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Categoría" required testid="cp-categoria-field">
                  <SelectInput value={form.categoria} onChange={(v) => { set("categoria", v); set("subcategoria", ""); }} options={PRODUCT_CATEGORIES.map((c) => ({ value: c.id, label: c.label }))} placeholder="Selecciona categoría" testid="cp-categoria" />
                </Field>
                <Field label="Subcategoría" testid="cp-subcategoria-field">
                  <SelectInput value={form.subcategoria} onChange={(v) => set("subcategoria", v)} options={subcats} placeholder={form.categoria ? "Selecciona subcategoría" : "Elige una categoría primero"} testid="cp-subcategoria" />
                </Field>
              </div>
              <Field label="Etiquetas" hint="Separadas por comas" testid="cp-etiquetas-field">
                <TextInput value={form.etiquetas} onChange={(v) => set("etiquetas", v)} placeholder="excel, presupuesto, apu" testid="cp-etiquetas" />
              </Field>
            </div>
          )}

          {paso === 1 && (
            <div className="space-y-5" data-testid="paso-archivos">
              <Field label="Imagen de portada" testid="cp-cover-field">
                <ImageUpload value={form.cover} onChange={(v) => set("cover", v)} scope="product_cover" aspect="aspect-[16/9]" label="Subir portada" testid="cp-cover" />
              </Field>

              <div>
                <span className="text-sm font-semibold text-slate-700">Galería de imágenes</span>
                <div className="mt-1.5 grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="cp-galeria">
                  {form.galeria.map((g, i) => (
                    <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-slate-200">
                      <img src={g} alt="" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => rmGaleria(i)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center"><X size={12} /></button>
                    </div>
                  ))}
                  {form.galeria.length < 6 && (
                    <button type="button" disabled={subiendoGaleria} onClick={() => galeriaRef.current?.click()} className="aspect-square rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 flex flex-col items-center justify-center text-slate-400 hover:border-amber-400 hover:text-amber-600 disabled:opacity-60" data-testid="cp-galeria-add">
                      {subiendoGaleria ? <Loader2 size={20} className="animate-spin" /> : <><ImagePlus size={20} /><span className="text-[10px] font-semibold mt-0.5">Añadir</span></>}
                    </button>
                  )}
                  <input ref={galeriaRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={readGaleria} />
                </div>
              </div>

              <Field label="Archivo digital descargable" hint="PDF, DOCX, XLSX, PPTX, ZIP, RAR, DWG, RVT, RFA, IFC, DYN o PBIX" testid="cp-archivo-field">
                <FileUpload
                  value={form.archivo}
                  nombre={form.archivoNombre}
                  onChange={(url, nombre) => { set("archivo", url); set("archivoNombre", nombre); }}
                  scope="product_file"
                  accept=".pdf,.docx,.xlsx,.pptx,.zip,.rar,.dwg,.rvt,.rfa,.ifc,.dyn,.pbix"
                  label="Seleccionar archivo del producto"
                  testid="cp-archivo"
                />
              </Field>

              <Field label="Enlace externo" hint="Opcional (Drive, sitio web…)" testid="cp-enlace-field">
                <TextInput value={form.enlaceExterno} onChange={(v) => set("enlaceExterno", v)} placeholder="https://…" testid="cp-enlace" />
              </Field>
              <Field label="Instrucciones de uso" testid="cp-instrucciones-field">
                <TextArea value={form.instrucciones} onChange={(v) => set("instrucciones", v)} rows={3} placeholder="Cómo instalar o usar el producto…" testid="cp-instrucciones" />
              </Field>
              <Field label="Versión del producto" testid="cp-version-field">
                <TextInput value={form.version} onChange={(v) => set("version", v)} placeholder="Ej. 1.0" testid="cp-version" />
              </Field>
            </div>
          )}

          {paso === 2 && (
            <div className="space-y-5" data-testid="paso-precio">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Precio en soles (S/)" required testid="cp-precio-field">
                  <TextInput value={form.precio} onChange={(v) => set("precio", v)} type="number" placeholder="0.00" testid="cp-precio" />
                </Field>
                <Field label="Precio promocional (S/)" hint="Opcional" testid="cp-preciopromo-field">
                  <TextInput value={form.precioPromo} onChange={(v) => set("precioPromo", v)} type="number" placeholder="0.00" testid="cp-preciopromo" />
                </Field>
              </div>
              <ComisionResumen precio={form.precio} />
              <Field label="Licencia de uso" testid="cp-licencia-field">
                <SelectInput value={form.licencia} onChange={(v) => set("licencia", v)} options={LICENCIAS_MKT} placeholder="Selecciona la licencia" testid="cp-licencia" />
              </Field>
              <label className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4 cursor-pointer" data-testid="cp-autoria-field">
                <input type="checkbox" checked={form.autoria} onChange={(e) => set("autoria", e.target.checked)} className="mt-0.5 w-5 h-5 accent-amber-600" data-testid="cp-autoria" />
                <span className="text-sm text-slate-600"><b className="text-slate-800">Confirmo la autoría y los derechos</b> de este producto digital y acepto que será revisado antes de su publicación.</span>
              </label>
            </div>
          )}
        </div>

        {/* Navegación del asistente */}
        <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <button onClick={() => (paso === 0 ? navigate("/marketplace/productos") : setPaso((p) => p - 1))} data-testid="cp-atras-btn" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl border border-slate-300 text-slate-700 font-semibold hover:bg-slate-100 transition-colors">
            <ChevronLeft size={16} /> {paso === 0 ? "Cancelar" : "Atrás"}
          </button>
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <button onClick={() => guardar("borrador")} data-testid="cp-borrador-btn" className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl border border-slate-300 text-slate-700 font-semibold hover:bg-slate-100 transition-colors">
              <Save size={16} /> Guardar borrador
            </button>
            {paso < PASOS.length - 1 ? (
              <button onClick={siguiente} data-testid="cp-siguiente-btn" className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl text-white font-bold shadow-sm hover:brightness-110 transition-all" style={{ backgroundColor: MKT_GOLD }}>
                Siguiente <ChevronRight size={16} />
              </button>
            ) : (
              <button onClick={() => guardar("en_revision")} data-testid="cp-enviar-btn" className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl text-white font-bold shadow-sm hover:brightness-110 transition-all" style={{ backgroundColor: MKT_GOLD }}>
                <Send size={16} /> Enviar a revisión
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
